import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  BookOpen,
  CheckCircle,
  Calendar,
  FileText,
  ShieldCheck,
  Users,
  Clock,
  ChevronDown,
  Download,
  Shield,
  CreditCard,
  Check,
  User,
  Mail,
  Phone,
  AlertCircle,
  Target,
  BarChart3,
  CheckCircle2,
  Star,
  PlayCircle,
  BarChart,
  MailIcon,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ContactForm from "../components/ContactForm";
import { constants } from "../constants";

// ✅ FULL 3-TIER SYSTEM - ALL EXAMS ALIGNED (English: Language | SAT/GRE: Academics)
const PLANS = {
  ielts: [
    {
      id: "ielts-beginner-feb01",
      tier: "beginner",
      name: "BUILD YOUR CONFIDENCE",
      subtitle: "6 months • English foundations for IELTS success",
      price: 18500,
      originalPrice: null,
      duration: "6 months",
      // startDate: "Feb 1",
      // seats: 15,
      // filled: 8,
      mostPopular: false,
      badge: "Fixed Batch",
      promise: "From zero confidence to consistent band practice",
      includes: [
        "Step-by-step IELTS question types",
        "Regular progress assessments",
        "Complete study materials",
        "2 full practice tests",
        "Personalised uni shortlist (3 universities)",
      ],
      // urgency: "7 seats left",
      idealFor:
        "Perfect for beginners building English confidence from scratch",
    },
    {
      id: "ielts-standard-feb01",
      tier: "standard",
      name: "MASTER IELTS SYSTEMATICALLY ⭐",
      subtitle: "4 months • Complete band score preparation",
      price: 22000,
      originalPrice: null,
      duration: "4 months",
      // startDate: "Feb 1",
      // seats: 12,
      // filled: 9,
      mostPopular: true,
      badge: "Most Popular",
      promise: "Structured path to your target band score",
      includes: [
        "Initial band assessment",
        "3 full practice tests",
        "Fixed batch • Live classes",
        "Complete study materials",
        "Personalised uni shortlist (5 universities)",
      ],
      // urgency: "3 seats left",
      idealFor: "Ideal for students ready for structured band improvement",
    },
    {
      id: "ielts-fasttrack-feb03",
      tier: "fasttrack",
      name: "PRECISE EXAM STRATEGY",
      subtitle: "6 weeks • Band-focused refinement",
      price: 28000,
      originalPrice: null,
      duration: "6 weeks",
      // startDate: "Feb 3",
      // seats: 10,
      // filled: 7,
      mostPopular: false,
      badge: "Deadline Driven",
      promise: "Exam-day precision for your target score",
      includes: [
        "Targeted exam techniques only",
        "Weekly full-length practice",
        "Priority feedback cycles",
        "Unlimited university shortlisting",
      ],
      // urgency: "3 seats left",
      idealFor: "Perfect for experienced test-takers with deadlines",
    },
  ],
  pte: [
    {
      id: "pte-beginner-feb01",
      tier: "beginner",
      name: "BUILD YOUR CONFIDENCE",
      subtitle: "6 months • PTE scoring patterns & templates",
      price: 19500,
      duration: "6 months",
      // startDate: "Feb 1",
      // seats: 15,
      // filled: 6,
      mostPopular: false,
      badge: "Fixed Batch",
      promise: "Master PTE's AI scoring from scratch",
      includes: [
        "PTE task templates & scoring logic",
        "Regular progress assessments",
        "Complete study materials",
        "2 full practice tests",
        "Personalised uni shortlist (3 universities)",
      ],
      // urgency: "9 seats left",
      idealFor: "Perfect for beginners mastering PTE's AI scoring",
    },
    {
      id: "pte-standard-feb01",
      tier: "standard",
      name: "MASTER PTE SYSTEMATICALLY ⭐",
      subtitle: "4 months • Complete score preparation",
      price: 23500,
      duration: "4 months",
      // startDate: "Feb 1",
      // seats: 12,
      // filled: 8,
      mostPopular: true,
      badge: "Most Popular",
      promise: "Predictable score gains through templates",
      includes: [
        "Initial score assessment",
        "3 full practice tests",
        "Fixed batch • Live classes",
        "Complete study materials",
        "Personalised uni shortlist (5 universities)",
      ],
      // urgency: "4 seats left",
      idealFor: "Ideal for students ready for structured PTE improvement",
    },
    {
      id: "pte-fasttrack-feb05",
      tier: "fasttrack",
      name: "PRECISE EXAM STRATEGY",
      subtitle: "6 weeks • Score-focused refinement",
      price: 29500,
      duration: "6 weeks",
      // startDate: "Feb 5",
      // seats: 10,
      // filled: 7,
      mostPopular: false,
      badge: "Deadline Driven",
      promise: "Final score optimization for exam day",
      includes: [
        "Targeted PTE techniques only",
        "Weekly full-length practice",
        "Priority feedback cycles",
        "Unlimited university shortlisting",
      ],
      // urgency: "3 seats left",
      idealFor: "Perfect for experienced PTE test-takers with deadlines",
    },
  ],
  duolingo: [
    {
      id: "duo-beginner-feb01",
      tier: "beginner",
      name: "BUILD YOUR CONFIDENCE",
      subtitle: "6 months • Duolingo adaptive test foundations",
      price: 12500,
      duration: "6 months",
      // startDate: "Feb 1",
      // seats: 15,
      // filled: 5,
      mostPopular: false,
      badge: "Fixed Batch",
      promise: "Crack adaptive testing patterns",
      includes: [
        "Adaptive question patterns",
        "Regular progress assessments",
        "Complete study materials",
        "2 full practice tests",
        "Personalised uni shortlist (3 universities)",
      ],
      // urgency: "10 seats left",
      idealFor: "Perfect for beginners mastering adaptive testing",
    },
    {
      id: "duo-standard-feb01",
      tier: "standard",
      name: "MASTER DUOLINGO SYSTEMATICALLY ⭐",
      subtitle: "4 months • Complete score preparation",
      price: 15500,
      duration: "4 months",
      // startDate: "Feb 1",
      // seats: 12,
      // filled: 8,
      mostPopular: true,
      badge: "Most Popular",
      promise: "Consistent DET scores for applications",
      includes: [
        "Initial score assessment",
        "3 full practice tests",
        "Fixed batch • Live classes",
        "Complete study materials",
        "Personalised uni shortlist (5 universities)",
      ],
      // urgency: "4 seats left",
      idealFor: "Ideal for students ready for structured DET preparation",
    },
    {
      id: "duo-fasttrack-feb07",
      tier: "fasttrack",
      name: "PRECISE EXAM STRATEGY",
      subtitle: "6 weeks • Score-focused refinement",
      price: 19500,
      duration: "6 weeks",
      // startDate: "Feb 7",
      // seats: 10,
      // filled: 6,
      mostPopular: false,
      badge: "Deadline Driven",
      promise: "Exam-ready DET performance",
      includes: [
        "Targeted DET techniques only",
        "Weekly full-length practice",
        "Priority feedback cycles",
        "Unlimited university shortlisting",
      ],
      // urgency: "4 seats left",
      idealFor: "Perfect for experienced Duolingo test-takers with deadlines",
    },
  ],
  toefl: [
    {
      id: "toefl-beginner-feb01",
      tier: "beginner",
      name: "BUILD YOUR CONFIDENCE",
      subtitle: "6 months • TOEFL iBT foundations & integrated tasks",
      price: 19500,
      duration: "6 months",
      // startDate: "Feb 1",
      // seats: 15,
      // filled: 7,
      mostPopular: false,
      badge: "Fixed Batch",
      promise: "Master iBT integrated tasks confidently",
      includes: [
        "iBT question types & integrated tasks",
        "Regular progress assessments",
        "Complete study materials",
        "2 full practice tests",
        "Personalised uni shortlist (3 universities)",
      ],
      // urgency: "8 seats left",
      idealFor: "Perfect for beginners building academic English confidence",
    },
    {
      id: "toefl-standard-feb01",
      tier: "standard",
      name: "MASTER TOEFL SYSTEMATICALLY ⭐",
      subtitle: "4 months • Complete iBT preparation",
      price: 23500,
      duration: "4 months",
      // startDate: "Feb 1",
      // seats: 12,
      // filled: 9,
      mostPopular: true,
      badge: "Most Popular",
      promise: "Complete iBT preparation roadmap",
      includes: [
        "Initial score assessment",
        "3 full practice tests",
        "Fixed batch • Live classes",
        "Complete study materials",
        "Personalised uni shortlist (5 universities)",
      ],
      // urgency: "3 seats left",
      idealFor: "Ideal for students ready for structured TOEFL improvement",
    },
    {
      id: "toefl-fasttrack-feb04",
      tier: "fasttrack",
      name: "PRECISE EXAM STRATEGY",
      subtitle: "6 weeks • iBT score refinement",
      price: 29500,
      duration: "6 weeks",
      // startDate: "Feb 4",
      // seats: 10,
      // filled: 7,
      mostPopular: false,
      badge: "Deadline Driven",
      promise: "iBT score refinement for deadlines",
      includes: [
        "Targeted iBT techniques only",
        "Weekly full-length practice",
        "Priority feedback cycles",
        "Unlimited university shortlisting",
      ],
      // urgency: "3 seats left",
      idealFor: "Perfect for experienced TOEFL test-takers with deadlines",
    },
  ],
  sat: [
    {
      id: "sat-beginner-feb01",
      tier: "beginner",
      name: "BUILD ACADEMIC FOUNDATIONS",
      subtitle: "6 months • School-to-SAT bridge program",
      price: 22500,
      originalPrice: null,
      duration: "6 months",
      // startDate: "Feb 1",
      // seats: 12,
      // filled: 4,
      mostPopular: false,
      badge: "Fixed Batch",
      promise: "Bridge school gaps to SAT readiness",
      includes: [
        "Math foundations (algebra, arithmetic, word problems)",
        "Reading fundamentals & vocabulary building",
        "Topic-level diagnostics",
        "Complete study materials",
        "2 SAT-style mocks (late-stage only)",
        "University shortlist + process overview (3 unis)",
      ],
      // urgency: "8 seats left",
      idealFor:
        "Perfect for students with weak school fundamentals or early starters (Class 9-10)",
    },
    {
      id: "sat-standard-feb01",
      tier: "standard",
      name: "MASTER SAT SYSTEMATICALLY ⭐",
      subtitle: "4 months • Complete SAT preparation cycle",
      price: 27500,
      originalPrice: null,
      duration: "4 months",
      // startDate: "Feb 1",
      // seats: 12,
      // filled: 9,
      mostPopular: true,
      badge: "Most Popular",
      promise: "Complete SAT score improvement cycle",
      includes: [
        "Full SAT syllabus (Math + Reading/Writing)",
        "Section-level diagnostics",
        "3 full-length SAT mocks",
        "Fixed batch • Live classes",
        "Complete study materials",
        "University shortlist + application flow (5 unis)",
      ],
      // urgency: "3 seats left",
      idealFor:
        "Ideal for students with decent school fundamentals aiming for competitive scores",
    },
    {
      id: "sat-fasttrack-feb03",
      tier: "fasttrack",
      name: "PRECISION SCORE OPTIMISATION",
      subtitle: "6-8 weeks • Diagnostic clearance required",
      price: 37500,
      originalPrice: null,
      duration: "6-8 weeks",
      // startDate: "Feb 3",
      // seats: 8,
      // filled: 6,
      mostPopular: false,
      badge: "Deadline Driven",
      promise: "Final SAT score maximization",
      includes: [
        "No concept teaching (strategy only)",
        "Weekly full-length SAT mocks",
        "Deep error analysis & time-management",
        "High-frequency faculty feedback",
        "Unlimited university application guidance",
      ],
      // urgency: "2 seats left",
      idealFor:
        "Perfect for retakers or students needing final score optimisation",
    },
  ],
  gre: [
    {
      id: "gre-beginner-feb01",
      tier: "beginner",
      name: "BUILD ACADEMIC FOUNDATIONS",
      subtitle: "6 months • Quant + Verbal foundation program",
      price: 28500,
      originalPrice: null,
      duration: "6 months",
      // startDate: "Feb 1",
      // seats: 10,
      // filled: 3,
      mostPopular: false,
      badge: "Fixed Batch",
      promise: "GRE foundations for MS/PhD applicants",
      includes: [
        "Quant basics (arithmetic, algebra, word problems)",
        "Verbal fundamentals & contextual vocabulary",
        "Topic-level diagnostics",
        "Complete study materials",
        "2 GRE-style mocks (towards end only)",
        "Eligibility + shortlist discussion (3 unis)",
      ],
      // urgency: "7 seats left",
      idealFor:
        "Perfect for students returning to academics or weak quant/verbal foundations",
    },
    {
      id: "gre-standard-feb01",
      tier: "standard",
      name: "MASTER GRE SYSTEMATICALLY ⭐",
      subtitle: "4 months • Complete GRE preparation cycle",
      price: 35000,
      originalPrice: null,
      duration: "4 months",
      // startDate: "Feb 1",
      // seats: 10,
      // filled: 8,
      mostPopular: true,
      badge: "Most Popular",
      promise: "Target GRE score for competitive programs",
      includes: [
        "Full Quant & Verbal syllabus",
        "Section-wise diagnostics",
        "3 full-length GRE mocks",
        "Fixed batch • Live classes",
        "Complete study materials",
        "University shortlist + application roadmap (5 unis)",
      ],
      // urgency: "2 seats left",
      idealFor:
        "Ideal for MS/PhD applicants with reasonable academic background",
    },
    {
      id: "gre-fasttrack-feb04",
      tier: "fasttrack",
      name: "PRECISION SCORE OPTIMISATION",
      subtitle: "6-8 weeks • Strict diagnostic clearance required",
      price: 45000,
      originalPrice: null,
      duration: "6-8 weeks",
      // startDate: "Feb 4",
      // seats: 8,
      // filled: 5,
      mostPopular: false,
      badge: "Deadline Driven",
      promise: "GRE retake score optimization",
      includes: [
        "Strategy-only approach (no concept teaching)",
        "Weekly full-length GRE mocks",
        "Quant speed + Verbal accuracy tuning",
        "Intensive error analysis",
        "Unlimited university application guidance",
      ],
      // urgency: "3 seats left",
      idealFor:
        "Perfect for retakers or deadline-driven applicants with GRE exposure",
    },
  ],
};

const TestPrepPage = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [formData, setFormData] = useState({ name: "", phone: "",email:"" });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [confirmEnrollment, setConfirmEnrollment] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [activeExam, setActiveExam] = useState("ielts");
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [plans, setPlans] = useState({});
  const { serverURL } = constants;
  const contactRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setReducedMotion(true);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 2000);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await fetch(`${serverURL}/api/plans/list`);
        const data = await res.json();

        const grouped = data.reduce((acc, plan) => {
          acc[plan.planCode] = acc[plan.planCode] || [];
          acc[plan.planCode].push(plan);
          return acc;
        }, {});

        setPlans(grouped);
        console.log(plans);
      } catch (err) {
        console.error("Failed to load plans", err);
      }
    };

    fetchPlans();
  }, []);

  const examDescriptions = {
    ielts: "Required for UK, Canada, Australia & most EU universities",
    pte: "Accepted by 3000+ institutions worldwide, fast results",
    toefl: "Preferred by US universities & academic programs",
    duolingo: "Adaptive test, accepted by 2000+ universities globally",
    sat: "Essential for US undergraduate admissions",
    gre: "Required for MS/PhD programs worldwide",
  };

  const handleExamChange = useCallback((exam) => {
    setActiveExam(exam);
    setShowPaymentModal(false);
    setSelectedPlan(null);
    setFormData({ name: "", phone: "", email:"" });
    setConfirmEnrollment(false);
  }, []);

  const scrollToContact = useCallback(() => {
    contactRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const MotionDiv = reducedMotion ? "div" : motion.div;
  const MotionButton = reducedMotion ? "button" : motion.button;

  const openPaymentModal = (plan) => {
    setSelectedPlan(plan);
    setShowPaymentModal(true);
    setConfirmEnrollment(false);
    setFormData({ name: "", phone: "", email:"" });
  };

  const currentPlans = plans[activeExam] || [];

  const handlePayment = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      alert("Please login to continue");
      window.location.href = "/login";
      return;
    }

    if (!formData.name || !formData.phone ||!formData.email || !confirmEnrollment) {
      alert("Please enter your name, phone, and confirm enrollment");
      return;
    }

    setPaymentLoading(true);

    try {
      const response = await fetch(`${serverURL}/api/orders/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          planId: selectedPlan._id,
        }),
      });

      console.log("Response:", response);
      if (!response.ok) throw new Error("Order creation failed");

      const order = await response.json();
      console.log(order)
      if (order.orderId) {
        console.log("Razorpay options:", order);
        const options = {
          key: order.key,
          amount: order.amount,
          currency: order.currency,
          name: "MSE Consultant",
          description: "Order Payment",
          order_id: order.orderId,
          handler: async (response) => {
            const verifyResponse = await fetch(
              `${serverURL}/api/orders/verify`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                  razorpayOrderId: order.orderId,
                  razorpayPaymentId: response.razorpay_payment_id,
                  razorpaySignature: response.razorpay_signature,
                  dbOrderId: order.dbOrderId,
                  email: formData.email
                }),
              },
            );
            console.log(verifyResponse)
            if (verifyResponse.ok) {
              if (typeof window.gtag === "function") {
                window.gtag("event", "conversion", {
                  send_to: "AW-759404552/XXXXXXX",
                });
              }
              alert(`🎉 Welcome to ${selectedPlan.name}!`);
              setShowPaymentModal(false);
              setFormData({ name: "", phone: "", email:"" });
              setConfirmEnrollment(false);
            } else {
              alert("Payment verification failed. Contact support.");
            }
          },
          prefill: {
            name: formData.name,
            contact: formData.phone,
          },
          theme: {
            color: "#dc2626",
          },
        };
        // const options = {
        //   key: order.key,
        //   order_id: order.orderId,

        //   amount: order.amount,
        //   currency: order.currency,
        //   name: "ExamPrep Academy",
        //   description: `${selectedPlan.name} (${activeExam.toUpperCase()}) • ${selectedPlan.startDate}`,

        //   prefill: {
        //     name: formData.name,
        //     contact: formData.phone,
        //   },
        //   theme: { color: "#1e293b" },
        //   handler: async (response) => {
        //     const verifyResponse = await fetch(
        //       `${serverURL}/api/orders/verify`,
        //       {
        //         method: "POST",
        //         headers: {
        //           "Content-Type": "application/json",
        //           Authorization: `Bearer ${token}`,
        //         },
        //         body: JSON.stringify({
        //           razorpayOrderId: order.orderId,
        //           razorpayPaymentId: response.razorpay_payment_id,
        //           razorpaySignature: response.razorpay_signature,
        //           dbOrderId: order.dbOrderId,
        //         }),
        //       },
        //     );

        //     if (verifyResponse.ok) {
        //       alert(`🎉 Welcome to ${selectedPlan.name}!`);
        //       setShowPaymentModal(false);
        //       setFormData({ name: "", phone: "" });
        //       setConfirmEnrollment(false);
        //     } else {
        //       alert("Payment verification failed. Contact support.");
        //     }
        //   },
        //   modal: { ondismiss: () => setPaymentLoading(false) },
        // };

        if (!window.Razorpay) {
          alert("Razorpay SDK not loaded");
          return;
        }

        const razorpay = new window.Razorpay(options);
        razorpay.open();
      }
    } catch (error) {
      console.error("Payment error:", error);
      alert("Payment setup failed. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Fixed Mobile CTA - SINGLE CALL */}
      <div className="lg:hidden fixed bottom-8 left-4 right-4 z-40 max-w-sm mx-auto">
        <MotionButton
          whileHover={reducedMotion ? {} : { scale: 1.02 }}
          className="w-full bg-slate-900 hover:bg-slate-800 text-white py-3 px-6 rounded-lg font-semibold text-sm border border-slate-800 transition-all"
          onClick={scrollToContact}
        >
          Talk to Counsellor
        </MotionButton>
      </div>

      {/* Desktop Sticky CTA - SINGLE CALL */}
      {showStickyCTA && (
        <div className="hidden lg:block fixed bottom-8 right-8 z-40">
          <MotionButton
            whileHover={reducedMotion ? {} : { scale: 1.05, y: -2 }}
            className="bg-white/90 backdrop-blur-xl text-slate-900 py-4 px-6 rounded-2xl font-bold text-sm shadow-2xl hover:shadow-3xl border border-slate-200 hover:border-slate-300 transition-all duration-200"
            onClick={scrollToContact}
          >
            Talk to Counsellor
          </MotionButton>
        </div>
      )}

      {/* Hero */}
      <section className="py-24 px-8 max-w-6xl mx-auto">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <MotionDiv
            initial={reducedMotion ? {} : { opacity: 0, y: 30 }}
            animate={reducedMotion ? {} : { opacity: 1, y: 0 }}
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-normal leading-tight mb-6 text-slate-900">
              Serious Test Preparation
              <span className="block text-3xl md:text-4xl lg:text-5xl text-slate-600 font-normal mt-4">
                for Study Abroad
              </span>
            </h1>
            <p className="text-2xl md:text-3xl text-slate-700 mb-8 max-w-2xl mx-auto leading-relaxed">
              IELTS • PTE • TOEFL • Duolingo • SAT • GRE
            </p>
            <p className="text-xl text-slate-600 mb-12 max-w-2xl mx-auto">
              Build → Master → Finish Strong | Faculty-led cohorts • Small
              batches • University-aligned
            </p>
            <div className="flex items-center justify-center gap-2 text-lg text-emerald-600 font-semibold mb-12">
              <Calendar className="w-5 h-5" />
              Start your preparation now! New batches begin every month.
            </div>
          </MotionDiv>
        </div>

        {/* Exam Selector */}
        <div className="max-w-2xl mx-auto mb-16 text-center">
          <div className="bg-slate-100 rounded-2xl p-1 inline-flex gap-1 flex-wrap justify-center">
            {["ielts", "pte", "toefl", "duolingo", "sat", "gre"].map((exam) => (
              <button
                key={exam}
                onClick={() => handleExamChange(exam)}
                className={`px-4 py-3 rounded-xl font-semibold text-sm transition-all md:px-6 ${
                  activeExam === exam
                    ? "bg-white shadow-lg text-slate-900"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {exam.toUpperCase()}
              </button>
            ))}
          </div>
          <p className="mt-6 text-sm text-slate-600 max-w-md mx-auto px-4">
            {examDescriptions[activeExam]}
          </p>
        </div>

        {/* 3-TIER PRICING TABLE */}
        <div className="max-w-6xl mx-auto">
          {currentPlans.length > 0 ? (
            <div className="grid lg:grid-cols-3 gap-8">
              {currentPlans.map((plan, idx) => (
                <MotionDiv
                  key={plan.id}
                  initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
                  animate={reducedMotion ? {} : { opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className={`group relative bg-white rounded-3xl p-8 transition-all duration-300 hover:shadow-2xl ${
                    plan.mostPopular
                      ? "lg:-mt-6 border-4 border-emerald-400 bg-emerald-50/50 shadow-2xl"
                      : "border-2 border-slate-100 hover:border-slate-200 hover:shadow-xl"
                  }`}
                >
                  {/* Plan Header - OUTCOME ZONE */}
                  <div className="text-center mb-8 pb-8 border-b-2 border-slate-100">
                    <h3 className="text-xl font-bold text-slate-900 mb-3">
                      {plan.name}
                    </h3>
                    <p className="text-slate-600 mb-6 text-base leading-relaxed">
                      {plan.subtitle}
                    </p>

                    <div className="text-4xl lg:text-5xl font-mono font-bold text-slate-900 mb-2">
                      ₹{plan.price.toLocaleString()}
                    </div>
                    <div className="text-lg text-slate-500 mb-6">
                      {plan.duration}
                    </div>

                    <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 text-sm font-bold px-4 py-2 rounded-full">
                      Starts {plan.startDate}
                    </div>

                    {plan.badge && (
                      <div className="mt-4 inline-flex items-center gap-1 bg-slate-100 text-slate-800 text-xs font-bold px-4 py-2 rounded-full">
                        {plan.badge}
                      </div>
                    )}

                    {plan.promise && (
                      <div className="mt-6 pt-6">
                        <p className="text-lg font-semibold text-slate-900 bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                          "{plan.promise}"
                        </p>
                      </div>
                    )}
                  </div>

                  {/* DELIVERY ZONE - REDUCED ICONS */}
                  <div className="space-y-6 mb-8">
                    <div className="space-y-3">
                      <div className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        Live Training
                      </div>
                      <div className="space-y-2 text-sm text-slate-700">
                        {plan.includes.slice(0, 3).map((feature, fidx) => (
                          <div
                            key={fidx}
                            className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl group-hover:bg-slate-100 transition-all"
                          >
                            <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                            <span className="leading-relaxed">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* SUPPORT ZONE */}
                    <div className="pt-6 border-t border-slate-100">
                      <div className="text-sm font-semibold text-slate-800 mb-3">
                        Personal Support
                      </div>
                      <div className="space-y-2 text-xs text-slate-600">
                        {plan.includes.slice(3).map((feature, fidx) => (
                          <div key={fidx} className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-2 flex-shrink-0" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {plan.idealFor && (
                    <div className="mb-8 p-4 bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-100 rounded-2xl">
                      <p className="text-sm font-semibold text-slate-800 text-center">
                        {plan.idealFor}
                      </p>
                    </div>
                  )}

                  <MotionButton
                    whileHover={reducedMotion ? {} : { scale: 1.02 }}
                    whileTap={reducedMotion ? {} : { scale: 0.98 }}
                    onClick={() => openPaymentModal(plan)}
                    className={`w-full rounded-2xl font-bold py-4 px-8 transition-all duration-200 text-base shadow-lg ${
                      plan.mostPopular
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white text-lg py-5 shadow-2xl"
                        : "bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 hover:border-slate-700"
                    }`}
                  >
                    {plan.mostPopular
                      ? "Choose Most Popular ⭐"
                      : "Choose This Plan"}
                  </MotionButton>

                  <div className="mt-6 pt-6 text-center border-t border-slate-100">
                    <p className="text-xs font-bold text-orange-600">
                      {plan.urgency}
                    </p>
                  </div>
                </MotionDiv>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="text-6xl text-slate-200 mb-4">📚</div>
              <p className="text-xl text-slate-500">Plans loading...</p>
            </div>
          )}
        </div>
      </section>

      {/* ✅ NEW: Reality Check Section */}
      <section className="py-20 px-8 bg-gradient-to-r from-rose-50/50 to-orange-50/50 border-y border-slate-100">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-normal mb-8 text-slate-900">
            Why 80% of students fail or retake
          </h2>
          <div className="grid md:grid-cols-3 gap-6 text-left max-w-4xl mx-auto">
            <div className="group">
              <div className="w-12 h-12 bg-rose-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-rose-200 transition-all">
                <AlertCircle className="w-6 h-6 text-rose-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-2 text-lg">
                No diagnostics
              </h4>
              <p className="text-sm text-slate-600">
                Wrong prep focus wastes 3-6 months
              </p>
            </div>
            <div className="group">
              <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-all">
                <BarChart3 className="w-6 h-6 text-orange-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-2 text-lg">
                Mock inflation
              </h4>
              <p className="text-sm text-slate-600">
                Easy practice builds false confidence
              </p>
            </div>
            <div className="group">
              <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-slate-200 transition-all">
                <FileText className="w-6 h-6 text-slate-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-2 text-lg">
                Generic material
              </h4>
              <p className="text-sm text-slate-600">
                Same PDFs for IELTS/PTE/TOEFL = no progress
              </p>
            </div>
          </div>
          <div className="mt-12 pt-12 border-t-2 border-slate-100">
            <p className="text-2xl font-bold text-slate-900 mb-4">
              Our system prevents exactly this.
            </p>
            <p className="text-lg text-slate-700 max-w-2xl mx-auto">
              Every student starts with diagnostics. Every mock gets faculty
              review.
            </p>
          </div>
        </div>
      </section>

      {/* How Our Prep Actually Works - UPGRADED */}
      <section className="py-24 px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-serif font-normal mb-6 text-slate-900">
              How our prep actually works
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center group">
              <div className="w-20 h-20 bg-emerald-100 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:bg-emerald-200 transition-all">
                <Target className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                1. Diagnose
              </h3>
              <p className="text-slate-600 text-lg leading-relaxed">
                Where you are now → Initial assessment shows exact gaps
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-blue-100 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-200 transition-all">
                <PlayCircle className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                2. Train
              </h3>
              <p className="text-slate-600 text-lg leading-relaxed">
                What we change weekly → Live classes fix gaps systematically
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-purple-100 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:bg-purple-200 transition-all">
                <CheckCircle2 className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                3. Validate
              </h3>
              <p className="text-slate-600 text-lg leading-relaxed">
                Exam-ready proof → Faculty-reviewed mocks match real cut-offs
              </p>
            </div>
          </div>

          <div className="p-8 bg-white/50 border-2 border-dashed border-slate-200 rounded-3xl text-center">
            <p className="text-lg text-slate-700 font-semibold max-w-2xl mx-auto leading-relaxed mb-4">
              <strong>We don't rush students into exams</strong> if diagnostics
              show risk — even if it delays intake.
            </p>
            <p className="text-slate-600">
              All mocks get faculty review. Designed to eliminate retakes.
            </p>
          </div>
        </div>
      </section>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && selectedPlan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100] flex items-center justify-center p-4"
            onClick={() => setShowPaymentModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="w-full max-w-lg bg-white/95 backdrop-blur-xl rounded-3xl border border-slate-200 shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-8">
                <div className="flex items-start justify-between mb-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                    {activeExam.toUpperCase()}
                  </div>
                  <div className="flex-1 ml-6">
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">
                      {selectedPlan.name}
                    </h2>
                    <div className="flex items-center gap-4 text-xl">
                      <span className="text-2xl font-mono font-bold text-emerald-600">
                        ₹{selectedPlan.price.toLocaleString()}
                      </span>
                      <span className="text-sm text-slate-500">
                        {selectedPlan.duration}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {selectedPlan.subtitle}
                    </p>
                  </div>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      WhatsApp Number *
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="+91 98765 43210"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <MailIcon className="w-4 h-4" />
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="example@gmail.com"
                      required
                    />
                  </div>

                  <div className="p-4 bg-emerald-50 border-2 border-emerald-100 rounded-2xl">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={confirmEnrollment}
                        onChange={(e) => setConfirmEnrollment(e.target.checked)}
                        className="w-5 h-5 text-emerald-600 rounded border-emerald-300 focus:ring-emerald-500"
                        required
                      />
                      <span className="text-sm text-slate-800 leading-relaxed">
                        I understand this reserves my seat for{" "}
                        <strong>{selectedPlan.name} </strong>
                        starting <strong>{selectedPlan.startDate}</strong> (
                        {selectedPlan.duration})
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mb-8 p-6 bg-blue-50 border border-blue-100 rounded-2xl text-sm text-slate-700 space-y-3">
                  <div className="flex items-start gap-2">
                    <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>
                      You'll receive assessment form by email immediately after
                      payment
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Phone className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>
                      Our team contacts you within 24 hours to confirm batch
                      details
                    </span>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-8">
                  <div className="flex flex-col lg:flex-row gap-4">
                    <MotionButton
                      whileHover={reducedMotion ? {} : { scale: 1.02 }}
                      disabled={
                        paymentLoading ||
                        !formData.name ||
                        !formData.phone ||
                        !confirmEnrollment
                      }
                      onClick={handlePayment}
                      className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 disabled:from-slate-400 disabled:to-slate-500 text-white py-4 px-8 rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-200 border border-emerald-600 flex items-center justify-center gap-3 disabled:cursor-not-allowed"
                    >
                      {paymentLoading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5" />
                          Pay Securely Now
                        </>
                      )}
                    </MotionButton>

                    <MotionButton
                      whileHover={reducedMotion ? {} : { scale: 1.02 }}
                      onClick={() => setShowPaymentModal(false)}
                      className="flex-1 border-2 border-slate-200 text-slate-700 py-4 px-8 rounded-2xl font-bold text-lg hover:bg-slate-50 hover:border-slate-300 transition-all duration-200"
                    >
                      Cancel
                    </MotionButton>
                  </div>

                  <div className="mt-6 pt-6 border-t border-slate-200 text-xs text-slate-500 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Shield className="w-4 h-4" />
                      Razorpay Secure Payments | 100+ assessments last intake
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Trust Signals */}
      <section className="py-24 px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-normal mb-4 text-slate-900">
              Your Journey: Build → Master → Finish Strong
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Faculty-led cohorts • Small batches (8-15 students) • Personalised
              university guidance
            </p>
            <div className="mt-12 grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-emerald-600 mb-2">
                  100+
                </div>
                <div className="text-sm text-slate-600">
                  Assessments conducted last intake
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  200+
                </div>
                <div className="text-sm text-slate-600">
                  Universities assisted
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  1x
                </div>
                <div className="text-sm text-slate-600">
                  Most students attempt exam once
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ✅ NEW: Micro FAQ */}
      <section className="py-20 px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-serif font-normal mb-12 text-center text-slate-900">
            Quick Answers
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6 bg-white rounded-2xl border border-slate-100 hover:shadow-lg transition-all">
              <h4 className="font-semibold text-slate-900 mb-3 text-lg">
                Can I switch plans later?
              </h4>
              <p className="text-sm text-slate-600">
                Yes, within first 2 weeks based on diagnostics. No extra cost if
                upgrading.
              </p>
            </div>
            <div className="p-6 bg-white rounded-2xl border border-slate-100 hover:shadow-lg transition-all">
              <h4 className="font-semibold text-slate-900 mb-3 text-lg">
                What if I miss classes?
              </h4>
              <p className="text-sm text-slate-600">
                Recordings + catch-up sessions available. Small batches ensure
                you never fall behind.
              </p>
            </div>
            <div className="p-6 bg-white rounded-2xl border border-slate-100 hover:shadow-lg transition-all">
              <h4 className="font-semibold text-slate-900 mb-3 text-lg">
                Works with college/work?
              </h4>
              <p className="text-sm text-slate-600">
                Yes. Evening/weekend batches. 6-8 hours/week total commitment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - STRENGTHENED */}
      <section
        ref={contactRef}
        className="py-24 px-8 bg-gradient-to-t from-slate-900 to-slate-800 text-white border-t border-slate-700"
      >
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-12 shadow-2xl">
            <h2 className="text-4xl md:text-5xl font-serif font-normal mb-6 bg-gradient-to-r from-white to-slate-200 bg-clip-text text-transparent">
              Choosing wrong plan costs time, money, attempts
            </h2>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Talk to our counsellor. Get matched perfectly. Register below.
            </p>
            <MotionButton
              whileHover={reducedMotion ? {} : { scale: 1.05 }}
              className="bg-white text-slate-900 px-12 py-6 rounded-2xl font-bold text-xl hover:bg-slate-50 transition-all duration-200 shadow-2xl hover:shadow-3xl border-2 border-white/20 w-full max-w-md mx-auto"
              onClick={scrollToContact}
            >
              Talk to Counsellor Now
            </MotionButton>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-8 pb-12">
        <ContactForm />
      </div>
    </div>
  );
};

export default TestPrepPage;
